import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// iter103 — Scroll restoration.
//
// Browser-Router doesn't restore scroll position on its own, so navigating
// from Lots → LotDetail → back to Lots dumped the user at the top of a
// 200-row list every time. Pairs naturally with the iter102 cache: we
// already paint the list instantly, now we put the user back where they
// were too.
//
// Behaviour:
//   • Browser back/forward (navigationType === 'POP') → restore the
//     previously-saved scroll position for that pathname.
//   • Any forward navigation (PUSH / REPLACE) → scroll to top, like browser
//     default for new pages.
//
// Positions are kept in-memory in a Map keyed by pathname. We persist them
// for the lifetime of the SPA session — survives Lot detail dialogs but
// resets on full reload, which matches user expectation.

export function useScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const positionsRef = useRef(new Map());
  const lastPathRef = useRef(null);

  // Save the *outgoing* page's scroll before we paint the new one. Runs on
  // pathname change (cleanup of the previous effect captures the about-to-
  // -leave path).
  useEffect(() => {
    const path = location.pathname;
    return () => {
      // We're navigating away from `path`; remember where the user was.
      positionsRef.current.set(path, window.scrollY);
    };
  }, [location.pathname]);

  // Restore (POP) or scroll-to-top (PUSH/REPLACE) after the new page paints.
  // We use a double-rAF so the new page has had a chance to commit its
  // first frame — important now that pages paint from the iter102 cache
  // instantly (otherwise we'd scroll BEFORE the rows existed and wind up
  // pinned to the bottom of an empty container).
  useEffect(() => {
    let cancelled = false;
    let r1 = 0, r2 = 0;
    if (navigationType === 'POP') {
      const y = positionsRef.current.get(location.pathname) ?? 0;
      r1 = window.requestAnimationFrame(() => {
        if (cancelled) return;
        r2 = window.requestAnimationFrame(() => {
          if (cancelled) return;
          window.scrollTo(0, y);
        });
      });
    } else {
      // Defer to next frame so the route children have a chance to mount
      // before we yank the viewport. Avoids a janky initial flash on slow
      // first paints.
      r1 = window.requestAnimationFrame(() => {
        if (!cancelled) window.scrollTo(0, 0);
      });
    }
    return () => {
      cancelled = true;
      if (r1) window.cancelAnimationFrame(r1);
      if (r2) window.cancelAnimationFrame(r2);
    };
    // location.key changes on every nav (incl. same-pathname replays)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}

// Drop-in mount component. Has to live inside the BrowserRouter tree.
export function ScrollRestoration() {
  useScrollRestoration();
  return null;
}
